//@ts-check
import React, { useEffect, useRef, useState } from "react";
import styles from "./admin-settings.module.scss";
import base from "../../styles/company.module.scss";
import { FetchResult, useMutation, useQuery } from "@apollo/client";
import { QUERY_COMPANY_ADMIN_SETTINGS_SCREEN } from "apps/company/api/graphQL/query";
import appInfo from "utility/app-info";
import { BUTTON_ACTION, BUTTON_VARIANT } from "apps/company/common/constants";
import { MUTATION_REMOVE_ADMIN } from "apps/company/api/graphQL/mutation";
import { ButtonInterface } from "apps/company/common/dataTypes";
import TemplateDialog from "apps/company/components/common/modal/TemplateDialog";
import { Button } from "components/ui";
import { PageLoading } from "apps/company/components/loading";
import { Member } from "../Members/dataTypes";
import { AdminSettingsScreen } from "./dataTypes";
import {
  AddAdminDialog,
  Input,
  StatusDialog,
} from "apps/company/components/common/modal";
import _ from "lodash";
import { Link } from "react-router-dom";
import { CommonModalProps } from "apps/company/components/common/modal/common";
import UserCheckIcon from "Assets/icon/icon-user-check.svg";
import { useAuth } from "providers/auth";
import PortalErrorPage from "apps/company/components/error";
import { logError } from "utility/Function";
import { Trans } from "react-i18next";
import { useTranslation } from "lib/i18n/use-translation";

const AdminSettings = () => {
  //HOOKS
  const { t } = useTranslation(["admin"]);
  const { loading, data, refetch, error } = useQuery(
    QUERY_COMPANY_ADMIN_SETTINGS_SCREEN,
    {
      variables: {
        appInfo,
      },
    }
  );

  const [removeAdmin] = useMutation(MUTATION_REMOVE_ADMIN);
  const { companyAdmin } = useAuth();

  useEffect(() => {
    //refetch to remove stale data from cache
    refetch();
    if (data) {
      const adminsData =
        data.companyPortalAdminSettingsScreen as AdminSettingsScreen;
      setTitle(adminsData.title);
      setSubtitle(adminsData.subTitle);
      setAddButton(adminsData.admins.title as ButtonInterface);
      setCompanyName(adminsData.companyName);
      setTableHeaders(adminsData.admins.labels);
      setAdmins(adminsData.admins.members);
      setMembers(adminsData.selectAdminList);
    }
  }, [data, loading, refetch]);

  //STATES
  const [title, setTitle] = useState<string>("");
  // eslint-disable-next-line
  const [subtitle, setSubtitle] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [addButton, setAddButton] = useState<ButtonInterface>({
    buttonAction: BUTTON_ACTION.ADD_MEMBER,
    buttonStyle: BUTTON_VARIANT.PRIMARY,
    buttonText: t("admin:action.add"),
  });
  const [tableHeaders, setTableHeaders] = useState<string[]>([]);
  const [admins, setAdmins] = useState<Member[]>([]);
  const [adminUserId, setAdminUserId] = useState<string>("");
  const [selectMembers, setMembers] = useState<
    Pick<Member, "id" | "member" | "firstName" | "lastName" | "email">[]
  >([]);

  const maxAdmins = useRef(5);

  // MODAL STATES
  const [modalInfo, setModalInfo] =
    useState<Pick<CommonModalProps, "title" | "description" | "action">>();
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] =
    useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] =
    useState<boolean>(false);

  if (loading) {
    return (
      <div className={styles["admins-container"]}>
        <PageLoading />
      </div>
    );
  }
  if (error) {
    return <PortalErrorPage pageTitle={t("admin:adminSettings.title")} />;
  }

  //HANDLERS
  const handleAddClick = () => {
    if (admins.length === maxAdmins.current) {
      setModalInfo({
        title: t("admin:adminSettings.maxAdminsTitle"),
        description: t("admin:adminSettings.maxAdminsDescription", {
          max: maxAdmins.current,
        }),
        action: t("admin:action.close"),
      });
      setIsTemplateModalOpen(true);
    } else {
      setIsAddAdminModalOpen(true);
    }
  };

  const handleActionButton = ({ adminUserId }: { adminUserId: string }) => {
    setAdminUserId(adminUserId);
    setModalInfo({
      title: t("admin:adminSettings.removeAdmin.title"),
      description: (
        <Trans i18nKey="admin:adminSettings.removeAdmin.description" />
      ),
      action: t("admin:action.remove"),
    });
    setIsTemplateModalOpen(true);
  };

  const handleDialogAction = async () => {
    if (adminUserId) {
      await removeAdmin({
        variables: {
          appInfo,
          adminUserId,
        },
      })
        .then((response) => {
          if (response.errors) {
            setModalInfo({
              title: t("admin:error.defaultTitle"),
              description: response.errors[0].message,
              action: t("admin:action.close"),
            });

            setIsTemplateModalOpen(true);
          }
          if (response.data) {
            if (response.data) {
              refetch();
              setModalInfo({
                title: t("admin:adminSettings.adminRemoved"),
                action: t("admin:action.close"),
              });
              setIsStatusModalOpen(true);
            }
          }
        })
        .catch((err) => {
          logError("Error removing admin", err);
          setModalInfo({
            title: t("admin:error.defaultTitle"),
            description: err.message,
            action: t("admin:action.close"),
          });

          setIsTemplateModalOpen(true);
        });

      setAdminUserId("");
    }
  };

  const handleAPIResponse = (response: FetchResult) => {
    if (response.data) {
      refetch();
      setModalInfo({
        title: t("admin:adminSettings.adminAdded"),
        action: t("admin:action.close"),
      });
      setIsStatusModalOpen(true);
    }
  };
  //JSX
  return (
    <>
      <AddAdminDialog
        onAPIResponse={handleAPIResponse}
        isOpen={isAddAdminModalOpen}
        title={t("admin:adminSettings.addAdmin.title")}
        description={t("admin:adminSettings.addAdmin.description")}
        action={t("admin:action.add")}
        members={selectMembers}
        onCloseAction={() => setIsAddAdminModalOpen(false)}
      />
      <TemplateDialog
        isOpen={isTemplateModalOpen}
        action={modalInfo?.action}
        description={modalInfo?.description}
        title={modalInfo?.title}
        onAction={() => handleDialogAction()}
        onCloseAction={() => setIsTemplateModalOpen(false)}
      />
      <StatusDialog
        isOpen={isStatusModalOpen}
        title={modalInfo?.title}
        action={modalInfo?.action}
        onCloseAction={() => setIsStatusModalOpen(false)}
      />
      <div className={styles["admins-container"]}>
        <div className={base["content"]}>
          <h1 className={base["main-title"]}>{title}</h1>
          {/* <p className={base["main-subtitle"]} dangerouslySetInnerHTML={{__html: subtitle}}></p>  */}
          <p className={base["main-subtitle"]}>
            <Trans
              i18nKey="admin:adminSettings.subtitle"
              components={[<Link to="/client/members" />]}
            />
          </p>
          <div className={styles["company-name"]}>
            <Input
              label={t("admin:adminSettings.companyNameLabel")}
              value={companyName}
              readOnly={true}
            />
          </div>
          {/* <div className="action-buttons">
              <Button onClick={handleAddClick}>{addButton.buttonText}</Button>
            </div> */}
          <div className={styles["table-title"]}>
            <h2 className="sans">{t("admin:adminSettings.tableHeading")}</h2>
            <div>
              <div className={styles["admin-counter"]}>
                <img src={UserCheckIcon} alt="user check icon" />
                {t("admin:adminSettings.counter", {
                  current: admins.length,
                  max: maxAdmins.current,
                })}
              </div>
              <Button onClick={handleAddClick}>{addButton.buttonText}</Button>
            </div>
            {/* <div className="line"></div> */}
          </div>
          <table>
            <thead>
              <tr className="sans">
                {tableHeaders.map((header: string) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            {loading && !data ? (
              <tbody>
                <tr>
                  <td colSpan={tableHeaders.length}>
                    <PageLoading />
                  </td>
                </tr>
              </tbody>
            ) : (
              <>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.member}</td>
                      <td>{admin.firstName}</td>
                      <td>{admin.lastName}</td>
                      <td>{admin.email}</td>
                      <td>{admin.timezone}</td>

                      {admin.action.buttonStyle === BUTTON_VARIANT.DISABLED ||
                      companyAdmin.id === admin.id ? (
                        <td className={`${styles["chip"]} sans`}>
                          <div className={styles["chip--default"]}>
                            {admin.action.buttonText}
                          </div>
                        </td>
                      ) : (
                        <td
                          onClick={() => {
                            handleActionButton({ adminUserId: admin.id });
                          }}
                          className={`${styles["action"]} sans`}
                        >
                          {admin.action.buttonText}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
          {_.isEmpty(admins) && (
            <div className={styles["common-no-results"]}>
              <h3>{t("admin:adminSettings.empty")}</h3>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminSettings;
